
import { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

const BrandBook = () => {
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  useState(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      console.log("BrandBook: No admin token found, redirecting to login");
      const returnPath = encodeURIComponent(location.pathname);
      navigate(`/admin/login?returnTo=${returnPath}`);
    } else {
      console.log("BrandBook: Admin token found, user is authenticated");
      setAuthenticated(true);
    }
  });

  if (!authenticated) {
    return (
      <div className="container mx-auto py-10">
        <Helmet>
          <title>Brand Book | edgefi Admin</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        <Card>
          <CardHeader>
            <CardTitle>Admin Access Required</CardTitle>
            <CardDescription>
              You need to be logged in as an administrator to view the Brand Book.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <p className="text-center mb-4">Please log in with an administrator account to continue.</p>
            <Button 
              onClick={() => navigate(`/admin/login?returnTo=${encodeURIComponent(location.pathname)}`)} 
              className="bg-edgefi-purple hover:bg-purple-800"
            >
              Go to Login Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10">
      <Helmet>
        <title>Brand Book | edgefi Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">edgefi Brand Book</h1>
        <p className="text-gray-600">Guidelines for design and messaging for edgefi.</p>
      </div>
      
      <Tabs defaultValue="mission">
        <TabsList className="mb-8 w-full grid grid-cols-6">
          <TabsTrigger value="mission">Mission</TabsTrigger>
          <TabsTrigger value="messaging">Messaging</TabsTrigger>
          <TabsTrigger value="logos">Logos</TabsTrigger>
          <TabsTrigger value="colors">Colors</TabsTrigger>
          <TabsTrigger value="typography">Typography</TabsTrigger>
          <TabsTrigger value="graphics">Graphics</TabsTrigger>
        </TabsList>
        
        <TabsContent value="mission">
          <Card>
            <CardHeader>
              <CardTitle>Mission Statement</CardTitle>
              <CardDescription>Our core purpose and vision</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <h3 className="text-xl font-semibold">Cybersecurity designed to evolve with the times.</h3>
              <p>We are here to help our customers build security operations that evolve with the times and are ahead of the chaos.</p>
              
              <h3 className="text-xl font-semibold">Contain the chaos.</h3>
              <p>Dealing with security is chaos. That's why we exist.</p>
              <p>We contain the chaos so you can focus on what you do best.</p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="messaging">
          <Card>
            <CardHeader>
              <CardTitle>Messaging and Tone of Voice</CardTitle>
              <CardDescription>How we communicate our brand</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p>edgefi's messaging should invoke a positive hopeful ethos.</p>
              <p><strong>Example:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>"Life is too short to be frustrated with IT services."</li>
                <li>"IT Problems shouldn't hold you back from being more productive and more profitable."</li>
              </ul>
              
              <h3 className="text-xl font-semibold mt-8">Tone of Voice Guidelines</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div className="p-4 border rounded-md bg-gray-50">
                  <h4 className="font-medium mb-2 text-edgefi-purple">Positive Tone</h4>
                  <p>A positive tone opens up the heart for a hopeful approach to the idea. When a client is afraid they often make mistakes that cost both the client and edgefi time and resources.</p>
                </div>
                
                <div className="p-4 border rounded-md bg-gray-50">
                  <h4 className="font-medium mb-2 text-edgefi-purple">Hope</h4>
                  <p>As an authority and leader in Cybersecurity and IT management, edgefi messaging should provide hope and a better future for both internal and external problem(s) that the customer might have or feel.</p>
                </div>
                
                <div className="p-4 border rounded-md bg-gray-50">
                  <h4 className="font-medium mb-2 text-edgefi-purple">External Need/Problem</h4>
                  <p>It's important to always address the external problem that the client might have. External problems are typically things that are tactical that prevent them from doing what the company does best.</p>
                </div>
                
                <div className="p-4 border rounded-md bg-gray-50">
                  <h4 className="font-medium mb-2 text-edgefi-purple">Internal Need/Problem</h4>
                  <p>An internal need or problem is an emotional feeling. The customer feels insecure that they don't have a plan and don't know where to start, and they are overwhelmed by it.</p>
                </div>
                
                <div className="p-4 border rounded-md bg-gray-50">
                  <h4 className="font-medium mb-2 text-edgefi-purple">Philosophical Problem</h4>
                  <p>A Philosophical problem is something that is just not right. It is wrong that hackers and bad guys do that to businesses and cause so much chaos and destruction.</p>
                </div>
                
                <div className="p-4 border rounded-md bg-gray-50">
                  <h4 className="font-medium mb-2 text-edgefi-purple">Authority (Guide not a Hero)</h4>
                  <p>As an authority in the space, our goal should be to position ourselves as a guide and not the hero. We use terms like "we will solve it for you" it tells the client that they will lose control and not be able to sustain the ongoing needs of the organization.</p>
                </div>
                
                <div className="p-4 border rounded-md bg-gray-50">
                  <h4 className="font-medium mb-2 text-edgefi-purple">Credibility</h4>
                  <p>We are a company that strives to consistently deliver what we promise through our interactions with others and the way we present ourselves online in order to build credibility and trust.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="logos">
          <Card>
            <CardHeader>
              <CardTitle>Logos</CardTitle>
              <CardDescription>Official edgefi logo variations</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="mb-4">The standard edgefi logo should be in purple or white but may be shown in black or chartreuse in some circumstances.</p>
              <p className="mb-4">When the entire logo is not needed, the logomark itself may be used (social media, company documents, presentations, etc).</p>
              
              <div className="bg-gray-100 p-4 rounded-md mb-6">
                <h3 className="font-semibold mb-2">Do not:</h3>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>Do not resize or change the position of the logomark/icon.</li>
                  <li>Do not use any other font for the text, regardless of how similar it looks.</li>
                  <li>Do not resize the image to new proportions or distort it in any way.</li>
                  <li>Do not change the colors (stick to the colors of the logo linked below!)</li>
                </ol>
              </div>
              
              <h3 className="font-semibold mt-8 mb-4">Full Logo Variations</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="flex flex-col items-center">
                  <div className="bg-gray-100 p-4 rounded-md flex justify-center items-center h-32 w-full mb-2">
                    <img src="/lovable-uploads/b15e860f-0f58-46de-9fa5-1f5ec1be93cf.png" alt="Purple Logo" className="max-h-full max-w-full" />
                  </div>
                  <span className="text-sm text-center">Purple Logo</span>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="bg-gray-800 p-4 rounded-md flex justify-center items-center h-32 w-full mb-2">
                    <img src="/lovable-uploads/774bb39e-8015-4fbb-97a6-b9efacb70453.png" alt="White Logo" className="max-h-full max-w-full" />
                  </div>
                  <span className="text-sm text-center">White Logo</span>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="bg-gray-100 p-4 rounded-md flex justify-center items-center h-32 w-full mb-2">
                    <img src="/lovable-uploads/1f7a115f-b868-4da8-b174-4e0f6f903bb0.png" alt="Black Logo" className="max-h-full max-w-full" />
                  </div>
                  <span className="text-sm text-center">Black Logo</span>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="bg-gray-100 p-4 rounded-md flex justify-center items-center h-32 w-full mb-2">
                    <img src="/lovable-uploads/946673b9-aa6f-4e0b-af68-52257c4d1af5.png" alt="Chartreuse Logo" className="max-h-full max-w-full" />
                  </div>
                  <span className="text-sm text-center">Chartreuse Logo</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="colors">
          <Card>
            <CardHeader>
              <CardTitle>Color Palette</CardTitle>
              <CardDescription>Official edgefi colors</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="flex flex-col">
                  <div className="h-24 rounded-t-md" style={{ backgroundColor: '#F7F7F6' }}></div>
                  <div className="bg-gray-100 p-3 rounded-b-md">
                    <p className="font-semibold">White-ish</p>
                    <p className="font-mono text-sm">#F7F7F6</p>
                  </div>
                </div>
                
                <div className="flex flex-col">
                  <div className="h-24 rounded-t-md" style={{ backgroundColor: '#C4F63A' }}></div>
                  <div className="bg-gray-100 p-3 rounded-b-md">
                    <p className="font-semibold">Chartreuse</p>
                    <p className="font-mono text-sm">#C4F63A</p>
                  </div>
                </div>
                
                <div className="flex flex-col">
                  <div className="h-24 rounded-t-md" style={{ backgroundColor: '#A4B6E2' }}></div>
                  <div className="bg-gray-100 p-3 rounded-b-md">
                    <p className="font-semibold">Powder Blue</p>
                    <p className="font-mono text-sm">#A4B6E2</p>
                  </div>
                </div>
                
                <div className="flex flex-col">
                  <div className="h-24 rounded-t-md" style={{ backgroundColor: '#9573D0' }}></div>
                  <div className="bg-gray-100 p-3 rounded-b-md">
                    <p className="font-semibold">Amethyst</p>
                    <p className="font-mono text-sm">#9573D0</p>
                  </div>
                </div>
                
                <div className="flex flex-col">
                  <div className="h-24 rounded-t-md" style={{ backgroundColor: '#6501E5' }}></div>
                  <div className="bg-gray-100 p-3 rounded-b-md">
                    <p className="font-semibold">edgefi Purple</p>
                    <p className="font-mono text-sm">#6501E5</p>
                  </div>
                </div>
                
                <div className="flex flex-col">
                  <div className="h-24 rounded-t-md" style={{ backgroundColor: '#272727' }}></div>
                  <div className="bg-gray-100 p-3 rounded-b-md">
                    <p className="font-semibold">Black</p>
                    <p className="font-mono text-sm">#272727</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="typography">
          <Card>
            <CardHeader>
              <CardTitle>Typography</CardTitle>
              <CardDescription>Official edgefi fonts and usage</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <p>Our company font is Poppins. Below are some examples and usage guidelines.</p>
              
              <div className="p-6 bg-gray-50 rounded-md">
                <p className="font-bold text-xl mb-4">Font Usage Guidelines</p>
                <ul className="space-y-2">
                  <li><strong>Poppins Semi Bold and Poppins Bold</strong> are mainly used for titles and H1/H2 headings.</li>
                  <li><strong>Poppins Regular and Poppins Medium</strong> are to be used as H3/H4 headings.</li>
                  <li><strong>Poppins Light</strong> should be used for all other forms of standard body text, ranging from blog posts, social media graphics, website design, stationary, presentations, brochures, and all other forms of general correspondence.</li>
                </ul>
              </div>
              
              <div className="py-4">
                <h3 className="font-semibold mb-4">Font Samples</h3>
                <div className="space-y-6">
                  <div>
                    <p style={{ fontFamily: 'Poppins', fontWeight: '700' }} className="text-2xl">Poppins Bold</p>
                    <p className="text-gray-500">Used for main titles and H1 headings</p>
                  </div>
                  
                  <div>
                    <p style={{ fontFamily: 'Poppins', fontWeight: '600' }} className="text-xl">Poppins Semi Bold</p>
                    <p className="text-gray-500">Used for H2 headings</p>
                  </div>
                  
                  <div>
                    <p style={{ fontFamily: 'Poppins', fontWeight: '500' }} className="text-lg">Poppins Medium</p>
                    <p className="text-gray-500">Used for H3 headings</p>
                  </div>
                  
                  <div>
                    <p style={{ fontFamily: 'Poppins', fontWeight: '400' }} className="text-base">Poppins Regular</p>
                    <p className="text-gray-500">Used for H4 headings</p>
                  </div>
                  
                  <div>
                    <p style={{ fontFamily: 'Poppins', fontWeight: '300' }} className="text-base">Poppins Light</p>
                    <p className="text-gray-500">Used for body text</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="graphics">
          <Card>
            <CardHeader>
              <CardTitle>Graphics Cheat Sheet</CardTitle>
              <CardDescription>Abstract imagery for brand visuals</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 p-6 rounded-md">
                <p>Our new brand changes include abstract imagery. If you're looking for a specific graphic to fit your deck, you can find it on this cheat sheet.</p>
                <p className="text-gray-500 mt-2 italic">/ in progress</p>
              </div>
              
              <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="border rounded-md p-4 flex flex-col items-center">
                  <div className="h-32 w-full bg-gray-100 rounded flex items-center justify-center mb-2">
                    <span className="text-gray-400">Image placeholder</span>
                  </div>
                  <p className="text-sm text-center">Abstract Pattern 1</p>
                </div>
                
                <div className="border rounded-md p-4 flex flex-col items-center">
                  <div className="h-32 w-full bg-gray-100 rounded flex items-center justify-center mb-2">
                    <span className="text-gray-400">Image placeholder</span>
                  </div>
                  <p className="text-sm text-center">Abstract Pattern 2</p>
                </div>
                
                <div className="border rounded-md p-4 flex flex-col items-center">
                  <div className="h-32 w-full bg-gray-100 rounded flex items-center justify-center mb-2">
                    <span className="text-gray-400">Image placeholder</span>
                  </div>
                  <p className="text-sm text-center">Abstract Pattern 3</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BrandBook;
